import { Box } from '@mui/material'
import styled from '@emotion/styled/macro'
import theme from 'theme'
import { DevicesTableContainer } from '../devices/devices.styles'

export const RulesViewContainer = styled(DevicesTableContainer)`
  padding: ${theme.spacing.medium}px;
  height: 100%;
  min-height: calc(100vh - 211px);
  display: flex;
  flex-direction: column;

  /* OV hides these by default and reveals them via JS once an async value lookup resolves.
     ER derives set-filter values from in-memory rules, so they're always ready — show them. */
  .ag-popup .ag-mini-filter,
  .ag-popup .ag-virtual-list-viewport,
  .ag-popup .ag-filter-apply-panel {
    visibility: visible !important;
  }

  /* Align the table rows with the header/InfoRow above. OV inherits a 12px right margin
     on .ag-root to make room for the absolutely-positioned vertical scrollbar; in ER we want
     the table to extend the full width of its wrapper and let the scrollbar render past it. */
  .ag-root-wrapper {
    overflow: visible !important;
  }
  .ag-root-wrapper .ag-root {
    margin-right: 0 !important;
  }

  /* OV's row border (#292328) is the same shade as RulesCard's background (#292128),
     so the borders render but blend in invisibly. Bump to a slightly lighter tone here. */
  .ag-center-cols-container .ag-row,
  .ag-pinned-left-cols-container .ag-row,
  .ag-pinned-right-cols-container .ag-row {
    &.ag-row-even,
    &.ag-row-odd {
      border-color: #3a3038 !important;

      html[data-theme='light'] & {
        border-color: ${theme.colors.dropdownBorder} !important;
      }
    }
  }

  /* Row styling — explicitly re-declared (not relying on DevicesTableContainer inheritance,
     which doesn't seem to apply here) so OV and ER look identical. */
  .ag-root-wrapper .ag-root .ag-body .ag-body-viewport {
    .ag-center-cols-container,
    .ag-pinned-left-cols-container,
    .ag-pinned-right-cols-container {
      .ag-row {
        border-radius: ${theme.attributes.borderRadius.tertiary};
        outline: 0;
        overflow: hidden;

        &.ag-row-even {
          background: transparent;
          border: 1px solid ${theme.colors.devicesRowBorder};

          &.ag-row-hover {
            background: ${theme.colors.devicesRowHoverBg} !important;
            &:before {
              background: ${theme.colors.devicesRowHoverBg} !important;
            }
          }
        }

        &.ag-row-odd {
          background: ${theme.colors.devicesRowOddBg};
          border: 1px solid ${theme.colors.devicesRowBorder};

          &.ag-row-hover {
            background: ${theme.colors.devicesRowHoverBg} !important;
            &:before {
              background: ${theme.colors.devicesRowHoverBg} !important;
            }
          }
        }

        .ag-cell {
          overflow: hidden;
          color: ${theme.colors.menuItemText};
          text-overflow: ellipsis;
          font-family: ${theme.typography.fontFamily.primary};
          font-size: ${theme.typography.fontSize.large};
          font-weight: ${theme.typography.fontWeight.bold};
          line-height: 20px;

          &[col-id='rule_name'] {
            font-weight: 700;
          }
        }
      }
    }
  }
`

export const RulesCard = styled(Box)`
  background-color: ${theme.colors.surfaceContainerBackground};
  border-radius: ${theme.attributes.borderRadius.tertiary};
  padding: ${theme.spacing.medium}px;
  display: flex;
  flex-direction: column;
  flex: 1;
  min-height: 0;
`

export const RulesSelectionActionBar = styled(Box)`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: ${theme.spacing.medium}px 0 0 0;
  margin: ${theme.spacing.medium}px -${theme.spacing.medium}px 0 -${theme.spacing.medium}px;
  padding-left: ${theme.spacing.medium}px;
  padding-right: ${theme.spacing.medium}px;
  border-top: 1px solid ${theme.colors.borderMedium};

  .selection-count {
    color: ${theme.colors.dropdownLabel};
    font-family: ${theme.typography.fontFamily.primary};
    font-size: ${theme.typography.fontSize.large};
    font-style: normal;
    font-weight: ${theme.typography.fontWeight.normal};
    line-height: 22px;

    span {
      color: ${theme.colors.menuItemText};
      font-weight: ${theme.typography.fontWeight.normal};
    }
  }
`

export const ViewHeader = styled(Box)`
  padding: 0 0 ${theme.spacing.xSmall}px 0;
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: ${theme.spacing.medium}px;

  .heading {
    display: flex;
    flex-direction: column;
    flex: 0 0 auto;
    min-width: 0;
  }

  .title-row {
    display: flex;
    align-items: center;
    gap: ${theme.spacing.xSmall}px;
  }

  .title {
    color: ${theme.colors.menuItemText};
    font-family: ${theme.typography.fontFamily.primary};
    font-size: 20px;
    font-style: normal;
    font-weight: 500;
    line-height: normal;
  }

  .subtitle {
    color: ${theme.colors.devicesSecondaryText};
    font-family: ${theme.typography.fontFamily.primary};
    font-size: 14px;
    font-style: normal;
    font-weight: 500;
    line-height: 22px;
    margin-top: ${theme.spacing.xSmall}px;
  }

  .filters-slot {
    flex: 1 1 auto;
    min-width: 0;
    display: flex;
    align-items: center;
    /* ViewHeader already has a 24px flex gap; add 16px so the visual distance
       between the chips and "Back to Main List" lands at 40px. */
    margin-right: ${theme.spacing.small}px;

    /* FilterChipsContainer assumes it owns a full-width row and reserves 24px on the right
       for the controls area. Inside the header it's already flex-sized, so undo that —
       and right-align the chips so the row sits flush against the action button. */
    .filter-chips-container {
      width: 100%;
      margin-right: 0;
      justify-content: flex-end;
    }
  }
`

export const HeaderActions = styled(Box)`
  display: flex;
  align-items: center;
  gap: ${theme.spacing.small}px;
`

export const InfoRow = styled(Box)`
  display: flex;
  justify-content: space-between;
  align-items: center;

  .MuiTypography-root {
    color: ${theme.colors.softMauve};
    font-family: ${theme.typography.fontFamily.primary};
    font-size: ${theme.typography.fontSize.large};
    font-weight: ${theme.typography.fontWeight.normal};
    line-height: 22px;

    span {
      color: ${theme.colors.neutral};
    }

    html[data-theme='light'] &,
    html[data-theme='light'] & span {
      color: #5c5865;
    }
  }
`

export const RulesEmptyState = styled(Box)`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: ${theme.spacing.medium}px;
  max-width: 420px;
  text-align: center;

  /* The grid's overlay class frames its content; this empty state sits on the card unframed. */
  &.ag-overlay-loading-center {
    border: none !important;
    background: transparent !important;
    box-shadow: none !important;
  }

  .icon-circle {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 128px;
    height: 128px;
    border-radius: 50%;
    border: 1px dashed ${theme.colors.strokePrimary};

    svg path {
      stroke: ${theme.colors.devicesSecondaryText};
    }
  }

  .title {
    color: ${theme.colors.neutral};
    font-family: ${theme.typography.fontFamily.primary};
    font-size: ${theme.typography.fontSize.xxxLarge};
    font-weight: ${theme.typography.fontWeight.bold};
  }

  .description {
    color: ${theme.colors.devicesSecondaryText};
    font-family: ${theme.typography.fontFamily.primary};
    font-size: ${theme.typography.fontSize.xLarge};
    font-weight: ${theme.typography.fontWeight.normal};
    line-height: 24px;
  }

  html[data-theme='light'] & .title {
    color: ${theme.colors.menuItemText};
  }
`
